import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { reportsApi, type ReportSummaryParams } from '@/lib/api/reports'
import type { ExportRequest, ReportPeriod } from '@/types'

const QUERY_KEYS = {
  summary: (familyId: string, params?: ReportSummaryParams) =>
    ['reports', 'summary', familyId, params] as const,
  export: (familyId: string, exportId: string) =>
    ['reports', 'export', familyId, exportId] as const,
}

export function useReportSummary(
  familyId: string | null,
  params?: ReportSummaryParams
) {
  return useQuery({
    queryKey: QUERY_KEYS.summary(familyId!, params),
    queryFn: () => reportsApi.getSummary(familyId!, params),
    enabled: !!familyId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export function useExportReport(familyId: string | null) {
  const queryClient = useQueryClient()

  const exportMutation = useMutation({
    mutationFn: (data: ExportRequest) => reportsApi.requestExport(familyId!, data),
    onSuccess: (job) => {
      // Store the export job in the cache
      queryClient.setQueryData(QUERY_KEYS.export(familyId!, job.id), job)
    },
  })

  return {
    requestExport: exportMutation.mutateAsync,
    isExporting: exportMutation.isPending,
    exportError: exportMutation.error,
  }
}

export function useExportStatus(
  familyId: string | null,
  exportId: string | null,
  options?: { enabled?: boolean; refetchInterval?: number | false }
) {
  return useQuery({
    queryKey: QUERY_KEYS.export(familyId!, exportId!),
    queryFn: () => reportsApi.getExportStatus(familyId!, exportId!),
    enabled: !!familyId && !!exportId && (options?.enabled ?? true),
    refetchInterval: (query) => ['COMPLETED', 'FAILED'].includes(query.state.data?.status ?? '') ? false : options?.refetchInterval ?? false,
    staleTime: 0, // Always fetch fresh status
  })
}

// Helper to get period display name
export function getPeriodDisplayName(period: ReportPeriod): string {
  switch (period) {
    case '7d':
      return 'Last 7 Days'
    case '30d':
      return 'Last 30 Days'
    case '90d':
      return 'Last 90 Days'
    default:
      return period
  }
}
